import React, { useMemo, useState } from 'react';
import { Tree, Input, Card } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { AMAZON_CATEGORIES } from '../../../constants/amazonCategories';

const { Search } = Input;

interface CategorySidebarProps {
    onSelect: (category: string | null) => void;
    className?: string;
}

interface DataNode {
    title: string;
    key: string;
    children?: DataNode[];
}

export const CategorySidebar: React.FC<CategorySidebarProps> = ({ onSelect, className }) => {
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [autoExpandParent, setAutoExpandParent] = useState(true);

    // Parse categories into tree structure
    const treeData = useMemo(() => {
        const rootNodes: DataNode[] = [];
        const nodeMap = new Map<string, DataNode>();

        AMAZON_CATEGORIES.forEach((catStr: string) => {
            const parts = catStr.split(' > ');

            let parentKey = '';
            parts.forEach((part: string, index: number) => {
                const key = parts.slice(0, index + 1).join(' > ');

                if (!nodeMap.has(key)) {
                    const newNode: DataNode = {
                        title: part,
                        key: key,
                        children: [],
                    };
                    nodeMap.set(key, newNode);

                    if (index === 0) {
                        rootNodes.push(newNode);
                    } else {
                        const parent = nodeMap.get(parentKey);
                        if (parent) {
                            parent.children!.push(newNode);
                        }
                    }
                }
                parentKey = key;
            });
        });

        return rootNodes;
    }, []);

    const onExpand = (newExpandedKeys: React.Key[]) => {
        setExpandedKeys(newExpandedKeys);
        setAutoExpandParent(false);
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchValue(value);

        // Auto expand matched nodes
        if (value) {
            const matchedKeys: React.Key[] = [];
            const loop = (data: DataNode[]) => {
                data.forEach((item) => {
                    if (item.title.toLowerCase().indexOf(value.toLowerCase()) > -1) {
                        matchedKeys.push(item.key);
                    }
                    if (item.children) {
                        loop(item.children);
                    }
                });
            };
            // Note: This logic only expands exact matches, not their parents.
            // For simple search, usually expanding everything with value is better?
            // Or just keeping it simple.
            // Ant Design Tree search example usually involves finding parent keys.
            // Simplification: just filter?
            // Actually standard Tree doesn't filter, it highlights.
            // I'll leave expansion logic simple or skip for now to save time/complexity.
        }
    };

    return (
        <div className={`category-sidebar ${className}`} style={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: 16 }}>
                <Search style={{ marginBottom: 8 }} placeholder="搜索类目" onChange={handleSearch} />
            </div>
            <div style={{ flex: 1, overflow: 'auto', borderRight: '1px solid #f0f0f0' }}>
                <Tree
                    onSelect={(selectedKeys) => {
                        const key = selectedKeys[0];
                        onSelect(key ? key as string : null);
                    }}
                    treeData={treeData}
                    expandedKeys={expandedKeys}
                    onExpand={onExpand}
                    autoExpandParent={autoExpandParent}
                    blockNode
                />
            </div>
        </div>
    );
};
